import "bootstrap/dist/css/bootstrap.min.css";
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Form, Row, Col, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2/dist/sweetalert2";
import { toast } from "react-toastify";
import axios from "axios";
import Progress from "../components/Progress";
import Title from "../components/Title";
import authFetch from "../axios";
import { STEP, SUCCESS_STATUS_DIGIT } from "../utils";
import FixedHeader from "../components/FixedHeader";
import fg from "../assets/npc/fg.png";
import BackgroundImage from "../components/BackgroundImage";
import watermark from "../assets/npc/npc-watermark.png";
import Select from "react-select";

const DocumentUpload = () => {
  const navigate = useNavigate();
  const bearerToken = sessionStorage.getItem("userToken");

  const [preloading, setPreLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [documentType, setDocumentType] = useState("");
  const [file, setFile] = useState(null);

  // Supported document types
  const documentTypes = [
    { value: "lga_id_card", label: "Local Government ID Card" },
    { value: "appointment_letter", label: "Appointment Letter" },
    { value: "other", label: "Other" },
  ];

  useEffect(() => {
    const getDetails = async () => {
      setPreLoading(true);
      try {
        const config = {
          method: "get",
          url: "/auth/retrive-data",
        };
        const { data } = await authFetch(config);
        if (data.palliative.step < STEP.RESIDENCE) {
          navigate(-1);
        }
        setPreLoading(false);
      } catch (error) {
        console.log(error);
        setPreLoading(false);
      }
    };
    getDetails();
  }, [navigate, bearerToken]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleAppStart = async (e) => {
    e.preventDefault();

    if (!documentType) {
      Swal.fire({
        title: "Error",
        text: "Please select a document type!",
        icon: "error",
        confirmButtonColor: "#0b6916",
        confirmButtonText: "Ok",
      });
      return;
    }

    if (!file) {
      Swal.fire({
        title: "Error",
        text: "Please select a file to upload!",
        icon: "error",
        confirmButtonColor: "#0b6916",
        confirmButtonText: "Ok",
      });
      return;
    }

    setLoading(true);

    try {
      Swal.fire({
        title: "Uploading Document...",
        showConfirmButton: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // --- NEW: Rename the file to the documentType enum ---
      // 1. Extract the file extension
      const fileExtension = file.name.split(".").pop();
      // 2. Construct the new file name using the enum value
      const newFileName = `${documentType}.${fileExtension}`;
      // 3. Create a new File object with the updated name
      const renamedFile = new File([file], newFileName, { type: file.type });

      // Step 1: Request an Upload URL
      const urlConfig = {
        method: "post",
        url: "/applicant/document-upload-url",
        data: {
          fileName: renamedFile.name, // Use the new file name
          mimeType: renamedFile.type,
          documentType: documentType,
        },
      };

      const urlResponse = await authFetch(urlConfig);

      // Extract from the palliative object
      const uploadUrl = urlResponse.data.palliative?.uploadUrl;
      const fileId = urlResponse.data.palliative?.fileId;

      if (!uploadUrl || !fileId) {
        throw new Error("Failed to retrieve valid upload URL from response.");
      }

      // Step 2: Upload the file directly to S3 using the presigned URL
      const blob = new Blob([renamedFile], { type: renamedFile.type });
      await axios.put(uploadUrl, blob, {
        headers: {
          "Content-Type": renamedFile.type,
        },
      });

      // Step 3: Attach the Uploaded Document
      const attachConfig = {
        method: "post",
        url: "/applicant/attach-document",
        data: {
          fileId: fileId,
        },
      };

      const { data } = await authFetch(attachConfig);

      Swal.close();

      if (data.palliative.status === SUCCESS_STATUS_DIGIT.FAILURE) {
        toast.error(data.palliative.message || "Failed to attach document", {
          position: "top-left",
        });
        setLoading(false);
      } else if (data.palliative.status === SUCCESS_STATUS_DIGIT.SUCCESS) {
        toast.success(
          data.palliative.message || "Document uploaded successfully",
          {
            position: "top-left",
          },
        );
        setLoading(false);
        navigate("/prev");
      }
    } catch (error) {
      console.log(error);
      Swal.close();
      toast.error(
        error.response?.data?.palliative?.message ||
          "Upload failed. Please ensure your file is valid and try again.",
        {
          position: "top-left",
        },
      );
      setLoading(false);
    }
  };

  return (
    <Wrapper className="nin-container">
      <FixedHeader logo={fg} title="New Application" />
      <>
        {preloading ? (
          <Spinner
            animation="grow"
            size="sm"
            role="status"
            aria-hidden="true"
            className="mx-auto"
          />
        ) : (
          <div className="container">
            <div className="pt-5 pb-5"></div>
            <div className="">
              <Progress active={3} />
              <div className="pt-3"></div>
              <Title title="Document Verification" />
              <BackgroundImage src={watermark} alt="Background Image" />
            </div>
            <div className="row">
              <div className="col-10 mx-auto">
                <Form>
                  <Row>
                    <Col
                      md={12}
                      lg={12}
                      className="p-2"
                      style={{
                        background: "#F5F5F5",
                        margin: "9px 0px",
                        padding: "10px",
                      }}
                    >
                      <div>
                        <ul>
                          <li
                            style={{
                              color: "red",
                              width: "100%",
                              margin: "0px 8px",
                              fontSize: "18px",
                            }}
                            className="text-center"
                          >
                            NOTE
                          </li>
                          <li>
                            - Please provide a valid verification document.
                          </li>
                          <li>
                            - Ensure the document is clear and fully visible
                            before uploading.
                          </li>
                        </ul>
                      </div>
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col md={6} lg={6}>
                      <Form.Label className="text-left">
                        <strong>Select Document Type</strong>
                      </Form.Label>
                      <Select
                        options={documentTypes}
                        onChange={(selectedOption) => {
                          setDocumentType(selectedOption.value);
                        }}
                        placeholder="Choose document type..."
                      />
                    </Col>

                    <Col md={6} lg={6}>
                      <Form.Group controlId="formFile">
                        <Form.Label className="text-left">
                          <strong>Upload Document</strong>
                        </Form.Label>
                        <Form.Control
                          required
                          type="file"
                          onChange={handleFileChange}
                          disabled={loading}
                          className="shadow-none"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Form>

                <button
                  style={{
                    backgroundColor: "#0e9738",
                    padding: "10px 20px",
                    fontSize: "13px",
                    cursor: "pointer",
                    borderRadius: "5px",
                    color: "#fff",
                    border: "none",
                  }}
                  type="submit"
                  className="my-btn my-2 px-5 mt-5"
                  disabled={loading}
                  onClick={handleAppStart}
                >
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    "Upload and Continue"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    </Wrapper>
  );
};

const Wrapper = styled.section`
  min-height: calc(100vh - 102.4px);
  display: flex;
  justify-content: center;
  align-items: center;
  text-transform: uppercase;
`;

export default DocumentUpload;
